/**
 * **Barrage** — the multi-weapon attack chain: which loadout is in play, the superheavy and
 * distinct-mount rules, the auxiliary rider, and the opener/continuation gates that decide whether
 * the chain may start and whether it may keep going. A Barrage commits its mounts on the opener
 * (`decisions/0049`, 2026-09-24 amendment); every later shot fires a weapon on them.
 */

import type { AttackLock, EncounterState, FrozenMount, WeaponProfile } from "@lancer-vtt/domain";
import { WEAPON_SIZE_AUXILIARY, WEAPON_SIZE_SUPERHEAVY, activeAttack, isItemDestroyed, isWeaponLoaded, weaponInstanceKey } from "@lancer-vtt/domain";
import type { EncounterIntent } from "@lancer-vtt/shared";
import { ok, err } from "@lancer-vtt/shared";
import { checkOrdnanceWindow } from "./ordnance";
import type { BudgetVerdict } from "./types";
import { checkLimited, checkLoaded, checkWeaponRecharge, noAttackRefusal } from "./weaponState";

/**
 * The frozen loadout, indexed by each weapon's **instance key** — the shared lookup the three
 * Barrage walls read. Keying on {@link weaponInstanceKey} (not the compendium `id`) is what lets
 * two mounts of the same weapon type be tracked independently: they share an `id` but have
 * distinct instance keys, so the map doesn't silently collapse them into one entry.
 * `weapon`/`fired` throughout these walls are instance keys too (the wire carries them).
 *
 * **Destroyed weapons are excluded** (Core p.80 System Trauma) — a destroyed weapon can't be a
 * Barrage primary OR a rider, so it's dropped from the candidate set entirely, exactly like
 * `selectWeapon`'s own filter. A mech whose every weapon is destroyed reads as weaponless
 * (`weapons.length === 0`), falling into the SAME weaponless-fallback path a mech that never had
 * a loadout hits.
 *
 * **Exported** so callers classifying a shot (primary vs rider, {@link isRiderShot}) reuse this
 * lookup instead of re-deriving their own weapon map.
 */
export function barrageLoadout(state: EncounterState, attackerId: string) {
  const attacker = state.combatants[attackerId];
  const weapons = (attacker?.base.weapons ?? []).filter(
    (w) => !isItemDestroyed(attacker?.current.resources, weaponInstanceKey(w)),
  );
  return { attacker, weapons, byId: new Map(weapons.map((w) => [weaponInstanceKey(w), w])) };
}

/**
 * How many attacks one use of `weapon` makes: {@link WeaponProfile.attacks} (an NPC weapon's
 * "can make two attacks at a time", tier-resolved at spawn), else 1. A multi-attack weapon fires
 * that many times inside one Skirmish or Barrage, each a separate attack against the same or a
 * different target; the attack lock lists it once per attack made.
 */
export function weaponAttackCount(weapon: Pick<WeaponProfile, "attacks"> | undefined): number {
  return weapon?.attacks ?? 1;
}

/** How many of `weaponKey`'s attacks this action has left, given `fired` (the lock lists a weapon once per attack it made). */
function attacksLeft(byId: Map<string, Pick<WeaponProfile, "attacks">>, fired: readonly string[], weaponKey: string): number {
  return weaponAttackCount(byId.get(weaponKey)) - fired.filter((f) => f === weaponKey).length;
}

/** Whether any of `fired` is a Superheavy on the loadout (a Superheavy fires ALONE — closes the Barrage). */
function firedSuperheavy(byId: Map<string, { mount?: string }>, fired: readonly string[]): boolean {
  return fired.some((id) => byId.get(id)?.mount === WEAPON_SIZE_SUPERHEAVY);
}

/** Whether `weaponKey` is Auxiliary-SIZED — its own frozen `WeaponProfile.mount`, never the `FrozenMount.type` it sits on. */
function isAuxSized(byId: Map<string, { mount?: string }>, weaponKey: string): boolean {
  return byId.get(weaponKey)?.mount === WEAPON_SIZE_AUXILIARY;
}

/**
 * The {@link FrozenMount} index whose `slots[]` carries `weaponKey` — mounts have no id of their
 * own; the array index IS the mount identity. `-1` when `mounts` is `undefined` (a non-mech
 * combatant) or `weaponKey` isn't slotted into any frozen mount (an Integrated weapon, which
 * carries its own `integrated:N` namespace and never appears in `base.mounts` at all).
 */
function mountIndexOf(mounts: readonly FrozenMount[] | undefined, weaponKey: string): number {
  if (!mounts) return -1;
  return mounts.findIndex((m) => m.slots.some((s) => s.weaponInstanceId === weaponKey));
}

/**
 * The **mount key** a weapon fires from — the identity a Barrage commits to and an
 * {@link AttackLock}'s `mounts` records (`decisions/0049`, 2026-09-24 amendment). `mount:<i>` for
 * the weapon's slot in `base.mounts[i]`; a weapon on no frozen mount (a frame-integrated weapon,
 * or any weapon of a combatant with no `base.mounts`) is its own mount and keys as its own weapon
 * instance key. An NPC freezes one synthetic mount per weapon (`spawnNpc`), so its weapons key as
 * `mount:<i>` too. The one derivation — the CLI, the room and the menu all read it.
 */
export function mountKeyOf(mounts: readonly FrozenMount[] | undefined, weaponKey: string): string {
  const idx = mountIndexOf(mounts, weaponKey);
  return idx >= 0 ? `mount:${idx}` : weaponKey;
}

/** One mount's weapons, as a Barrage/Skirmish commits to them — see {@link attackMounts}. */
export type AttackMount = {
  /** The {@link mountKeyOf} key. */
  key: string;
  /** The mount's non-destroyed weapons, in loadout order. */
  weapons: WeaponProfile[];
};

/**
 * `attackerId`'s loadout grouped by {@link mountKeyOf} — the list a mount-first pick screen
 * offers and a committed lock's weapon list renders from. Destroyed weapons are excluded (the
 * {@link barrageLoadout} filter); a mount whose every weapon is destroyed does not appear.
 */
export function attackMounts(state: EncounterState, attackerId: string): AttackMount[] {
  const { attacker, weapons } = barrageLoadout(state, attackerId);
  const groups = new Map<string, WeaponProfile[]>();
  for (const w of weapons) {
    const key = mountKeyOf(attacker?.base.mounts, weaponInstanceKey(w));
    groups.set(key, [...(groups.get(key) ?? []), w]);
  }
  return [...groups].map(([key, ws]) => ({ key, weapons: ws }));
}

/**
 * Why `weapon` **can't fire right now**, or `undefined` when it can — the per-weapon blockers a
 * committed mount's weapon list greys out and the auto-close predicate skips: Loading (unloaded),
 * Recharge (discharged), Limited (out of charges), and Ordnance (read against `ordnanceWindow`,
 * the lock's turn-virgin snapshot, or the live window for a fresh pick). The same gate functions
 * the room's per-shot ladder runs, so a greyed reason matches the rejection word for word. Range,
 * line of sight and targeting are not consulted: they depend on a target nobody has named yet.
 */
export type WeaponFireBlockArgs = {
  state: EncounterState;
  attackerId: string;
  /** The weapon **instance key**. */
  weapon: string;
  /** Is the Ordnance window open for this action? */
  ordnanceWindow: boolean;
};

export function weaponFireBlock(args: WeaponFireBlockArgs): string | undefined {
  const { state, attackerId, weapon, ordnanceWindow } = args;
  const { attacker, byId } = barrageLoadout(state, attackerId);
  const w = byId.get(weapon);
  if (!attacker || !w) return undefined;
  if (w.noAttack === true) return `${w.name} fires only through its own reaction`;
  const loaded = checkLoaded({ state, attacker: attackerId, weapon });
  if (!loaded.ok) return loaded.error;
  const recharge = checkWeaponRecharge({ state, attacker: attackerId, weapon });
  if (!recharge.ok) return recharge.error;
  const limited = checkLimited({ state, attacker: attackerId, weapon });
  if (!limited.ok) return limited.error;
  if (w.ordnance && !ordnanceWindow) {
    return `${w.name} is Ordnance — can only be fired before ${attacker.name} moves or takes any other action this turn (excepting Protocols)`;
  }
  return undefined;
}

/**
 * May `weaponKey` fire on a mount that has already fired `firedOnMount` this action? A mount
 * fires at most **two** weapons — one primary plus one Auxiliary rider (Core p.70 Skirmish,
 * p.71 Barrage) — and at most one of the two may be non-Auxiliary. In any order: an Aux may fire
 * before its mount's Main (`decisions/0049`, 2026-09-24 amendment). A weapon that already fired
 * may fire again only while it has attacks left ({@link weaponAttackCount}).
 */
function canFollowOnMount(byId: Map<string, { mount?: string; attacks?: number }>, firedOnMount: readonly string[], weaponKey: string): boolean {
  if (firedOnMount.includes(weaponKey)) return attacksLeft(byId, firedOnMount, weaponKey) > 0;
  const distinct = [...new Set(firedOnMount)];
  if (distinct.length === 0) return true;
  if (distinct.length >= 2) return false;
  return isAuxSized(byId, weaponKey) || isAuxSized(byId, distinct[0]!);
}

/** The weapons in `fired` that sit on `key`'s mount. */
function firedOnMountOf(mounts: readonly FrozenMount[] | undefined, fired: readonly string[], key: string): string[] {
  return fired.filter((f) => mountKeyOf(mounts, f) === key);
}

/**
 * Is this shot the **Aux rider** of its mount — the attack that deals no bonus damage (Core p.70
 * Skirmish: "a different AUXILIARY weapon on the same mount. That weapon doesn't deal bonus
 * damage"; p.71 Barrage: "These AUXILIARY weapons don't deal bonus damage")? RAW names the
 * Auxiliary as the no-bonus weapon, so a Main is never the rider. An Aux is the rider only once
 * it is paired: when another weapon on its mount already fired this action (its Main, or the
 * first Aux of a two-Aux mount). An Aux that fires FIRST is not stamped here, because the pairing
 * isn't known yet — if the action ends or the Barrage converts before its Main fires, it was the
 * mount's only shot and keeps its bonus damage. If the Main does follow, {@link pairedAuxRiderOf}
 * names that earlier Aux as the rider on the Main's resolution (`decisions/0049`, 2026-09-25
 * amendment).
 */
export type IsRiderShotArgs = {
  state: EncounterState;
  attackerId: string;
  /** Weapons already fired this action, before this shot. */
  fired: readonly string[];
  weaponKey: string;
};

export function isRiderShot(args: IsRiderShotArgs): boolean {
  const { state, attackerId, fired, weaponKey } = args;
  const { attacker, byId } = barrageLoadout(state, attackerId);
  if (!attacker || !isAuxSized(byId, weaponKey)) return false;
  return firedOnMountOf(attacker.base.mounts, fired, mountKeyOf(attacker.base.mounts, weaponKey)).some((f) => f !== weaponKey);
}

/**
 * The **Aux-then-Main pairing**: when this shot is a non-Auxiliary weapon and an Aux on its mount
 * already fired this action, that earlier Aux is the mount's rider (Core p.70/p.71) and this Main
 * the primary. Returns the earlier Aux's instance key, else `undefined`. The earlier Aux's own
 * resolution is already recorded without `noBonus` (the feed is append-only), so the room records
 * the pairing on the Main's resolution instead (`about.auxRider`), where the narrator reads it.
 */
export function pairedAuxRiderOf(args: IsRiderShotArgs): string | undefined {
  const { state, attackerId, fired, weaponKey } = args;
  const { attacker, byId } = barrageLoadout(state, attackerId);
  if (!attacker || isAuxSized(byId, weaponKey)) return undefined;
  return firedOnMountOf(attacker.base.mounts, fired, mountKeyOf(attacker.base.mounts, weaponKey)).find((f) => f !== weaponKey && isAuxSized(byId, f));
}

/**
 * Does `firedWeapon`'s mount hold **another weapon that can still fire alongside it** in a
 * Skirmish (Core p.70: the primary plus "a DIFFERENT AUXILIARY weapon on the SAME mount", in
 * either order)? This is the **conditional-open** gate a Skirmish runs after its first shot:
 * `true` opens a `skirmish` lock on that mount, `false` (the common case — a one-weapon mount)
 * opens nothing. The partner must be unfired, pair legally ({@link canFollowOnMount}) and not be
 * blocked ({@link weaponFireBlock}, against the live Ordnance window of the pre-shot state).
 * A multi-attack weapon ({@link weaponAttackCount} above 1) is its own partner: it always opens the
 * lock, so its later attacks fire as continuations.
 */
export function hasEligibleSkirmishRider(state: EncounterState, attackerId: string, firedWeapon: string): boolean {
  if (weaponAttackCount(barrageLoadout(state, attackerId).byId.get(firedWeapon)) > 1) return true;
  const { attacker, weapons, byId } = barrageLoadout(state, attackerId);
  if (!attacker) return false;
  const mounts = attacker.base.mounts;
  const key = mountKeyOf(mounts, firedWeapon);
  const ordnanceWindow = checkOrdnanceWindow(state, attackerId);
  return weapons.some((w) => {
    const k = weaponInstanceKey(w);
    return (
      k !== firedWeapon &&
      mountKeyOf(mounts, k) === key &&
      canFollowOnMount(byId, [firedWeapon], k) &&
      weaponFireBlock({ state, attackerId, weapon: k, ordnanceWindow }) === undefined
    );
  });
}

/**
 * Is `intent` a **mid-attack-lock continuation** of `lock` — the shared opener-vs-continuation
 * disambiguation for Barrage, Skirmish's second weapon, and Full Tech? `true` for:
 *   - a `request_barrage` shot from the lock's own acting attacker, while a **Barrage** lock is
 *     open (the per-shot weapon legality is {@link checkBarrageContinuation}'s job, separately) — a
 *     Barrage shot during a Skirmish or Full Tech lock is a new action the mid-lock gate rejects; or
 *   - a non-reaction `request_attack` shot from the lock's own acting attacker, while a
 *     **Skirmish** lock is open AND the shot's weapon is on the lock's mount and pairs legally
 *     with what already fired there ({@link canFollowOnMount}: an unfired partner, or a
 *     multi-attack weapon with attacks left) — anything else
 *     (a different mount, a third weapon, a non-attack action) is a genuine "another action" the
 *     mid-lock gate rejects. A blocked weapon (Unloaded, Ordnance) still reads as a continuation,
 *     so its own gate rejects it with its own reason; or
 *   - a `request_full_tech` pick from the lock's own acting attacker, while a **Full Tech** lock is
 *     open — ANY known Quick Tech option ({@link checkFullTechContinuation} is the per-pick check).
 * `false` whenever `lock` is `null` (nothing is open) or the turn isn't `acting`.
 */
export type IsAttackLockContinuationArgs = {
  state: EncounterState;
  intent: EncounterIntent;
  lock: AttackLock | null;
};

export function isAttackLockContinuation(args: IsAttackLockContinuationArgs): boolean {
  const { state, intent, lock } = args;
  if (lock === null) return false;
  const turn = state.activation.turn;
  if (turn.phase !== "acting") return false;
  if (intent.type === "request_barrage" && lock.action === "barrage") {
    return turn.actor === intent.attacker;
  }
  if (intent.type === "request_full_tech" && lock.action === "full_tech") {
    return turn.actor === intent.actor;
  }
  if (intent.type === "request_attack" && !intent.reaction && lock.action === "skirmish") {
    if (turn.actor !== intent.attacker || intent.weapon === undefined) return false;
    const { attacker, byId } = barrageLoadout(state, intent.attacker);
    if (!byId.has(intent.weapon)) return false;
    const mounts = attacker?.base.mounts;
    const key = mountKeyOf(mounts, intent.weapon);
    const lockMount = lock.mounts?.[0] ?? (lock.weapons[0] !== undefined ? mountKeyOf(mounts, lock.weapons[0]) : undefined);
    if (key !== lockMount) return false;
    return canFollowOnMount(byId, firedOnMountOf(mounts, lock.weapons, key), intent.weapon);
  }
  return false;
}

/**
 * May `attacker` **open** a Barrage with `weapon`? The opener's weapon-allotment wall, run before
 * any dice (the Full-action budget is the separate `checkBudget`; range is the room's per-shot
 * gate; the declared mount commitment is {@link checkBarrageCommit}). The weapon must exist on the
 * frozen loadout — a Barrage names a real loadout weapon — UNLESS the mech is **weaponless** (no
 * frozen weapons at all), in which case the resolver's single fallback strike still resolves (so a
 * weaponless unit Barrages once). A Superheavy is a legal opener (it then fires alone —
 * auto-closed by the no-legal-remaining predicate). **Loading**: an unloaded Loading weapon can't
 * open a Barrage either. Returns a verdict the room surfaces (a rejection is a logged no-op
 * before any dice).
 */
export function checkBarrageOpener(state: EncounterState, attackerId: string, weapon: string): BudgetVerdict {
  const { attacker, weapons, byId } = barrageLoadout(state, attackerId);
  if (!attacker) return err(`barrage: ${attackerId} is not on the map`);
  if (weapons.length > 0 && !byId.has(weapon)) {
    return err(`barrage: ${attackerId} has no weapon "${weapon}" on its loadout`);
  }
  const w = byId.get(weapon);
  if (w?.noAttack === true) return err(`barrage: ${noAttackRefusal(w)}`);
  if (w?.loading && !isWeaponLoaded(attacker.current.resources, weapon)) {
    return err(`barrage: "${w.name}" is not loaded (Loading) — reload with Stabilize before firing again`);
  }
  return ok(undefined);
}

/**
 * May a Barrage opener **commit** to `mounts` (`RequestBarrage.mounts`, `decisions/0049`'s
 * 2026-09-24 amendment)? Run beside {@link checkBarrageOpener}, before any dice, only when the
 * opener names its mounts:
 *   - one or two distinct mount keys ({@link mountKeyOf}), each a mount of the loadout;
 *   - a mount carrying a **Superheavy** is committed alone, and `weapon` must be that Superheavy
 *     (it fires alone);
 *   - `weapon` sits on one of the committed mounts;
 *   - every committed mount holds at least one weapon that can fire now ({@link weaponFireBlock},
 *     against the live Ordnance window) — "a mount can be picked if one of its weapons can fire."
 */
export type CheckBarrageCommitArgs = {
  state: EncounterState;
  attackerId: string;
  /** The opener shot's weapon instance key. */
  weapon: string;
  /** The committed mount keys. */
  mounts: readonly string[];
};

export function checkBarrageCommit(args: CheckBarrageCommitArgs): BudgetVerdict {
  const { state, attackerId, weapon, mounts } = args;
  const { attacker, byId } = barrageLoadout(state, attackerId);
  if (!attacker) return err(`barrage: ${attackerId} is not on the map`);
  if (mounts.length < 1 || mounts.length > 2) {
    return err(`barrage: commit one or two mounts (got ${mounts.length})`);
  }
  if (new Set(mounts).size !== mounts.length) return err(`barrage: the committed mounts must be distinct`);
  const groups = new Map(attackMounts(state, attackerId).map((g) => [g.key, g.weapons]));
  const ordnanceWindow = checkOrdnanceWindow(state, attackerId);
  for (const key of mounts) {
    const ws = groups.get(key);
    if (ws === undefined) return err(`barrage: ${attacker.name} has no mount "${key}"`);
    const sh = ws.find((w) => w.mount === WEAPON_SIZE_SUPERHEAVY);
    if (sh !== undefined) {
      if (mounts.length > 1) return err(`barrage: a Superheavy's mount ("${key}") is committed alone — a Superheavy fires alone`);
      if (weaponInstanceKey(sh) !== weapon) return err(`barrage: "${sh.name}" is a Superheavy — it fires alone, so it opens this Barrage`);
    }
    const blocks = ws.map((w) => weaponFireBlock({ state, attackerId, weapon: weaponInstanceKey(w), ordnanceWindow }));
    if (!blocks.includes(undefined)) return err(`barrage: no weapon on mount "${key}" can fire right now — ${blocks[0]}`);
  }
  if (!mounts.includes(mountKeyOf(attacker.base.mounts, weapon))) {
    return err(`barrage: "${byId.get(weapon)?.name ?? weapon}" is not on a committed mount (${mounts.join(", ")})`);
  }
  return ok(undefined);
}

/**
 * May `attacker` **continue** an in-progress Barrage with `weapon`? The continuation's
 * weapon-allotment wall (RAW Core p.71; `decisions/0049`'s 2026-09-24 amendment), run before any
 * dice (no second charge; range is the room's per-shot gate):
 *   - the weapon must exist on the frozen loadout and **not already have fired** this Barrage,
 *     unless it is a multi-attack weapon with attacks left ({@link weaponAttackCount});
 *   - it must **not be a Superheavy**, and **no Superheavy may already have fired** (a Superheavy
 *     opens its Barrage and fires alone);
 *   - it sits on a **committed mount** (`mounts`, named by the opener) and pairs legally with
 *     what already fired there ({@link canFollowOnMount}: at most two per mount, at most one
 *     non-Auxiliary, any order);
 *   - **Loading**: an unloaded weapon can't fire.
 * `mounts` absent (a weaponless Barrage's lock) reads the committed set off `fired`.
 */
export type CheckBarrageContinuationArgs = {
  state: EncounterState;
  attackerId: string;
  /** The weapon this continuation shot would fire. */
  weapon: string;
  /** The weapons already fired this Barrage (off the mid-attack lock). */
  fired: readonly string[];
  /** The lock's committed mount keys (`AttackLock.mounts`). */
  mounts?: readonly string[];
};

export function checkBarrageContinuation(args: CheckBarrageContinuationArgs): BudgetVerdict {
  const { state, attackerId, weapon, fired } = args;
  const { attacker, weapons, byId } = barrageLoadout(state, attackerId);
  if (!attacker) return err(`barrage: ${attackerId} is not on the map`);
  if (weapons.length > 0 && !byId.has(weapon)) {
    return err(`barrage: ${attackerId} has no weapon "${weapon}" on its loadout`);
  }
  const repeat = fired.includes(weapon);
  if (repeat && attacksLeft(byId, fired, weapon) <= 0) {
    return err(`barrage: weapon "${weapon}" already fired this Barrage — a Barrage fires each weapon once`);
  }
  if (!repeat && byId.get(weapon)?.mount === WEAPON_SIZE_SUPERHEAVY) {
    return err(`barrage: a Superheavy weapon ("${weapon}") fires alone — it can't be added to an in-progress Barrage`);
  }
  if (!repeat && firedSuperheavy(byId, fired)) {
    return err(`barrage: a Superheavy already fired — a Superheavy Barrage fires that one weapon alone`);
  }
  const mounts = attacker.base.mounts;
  const committed = args.mounts ?? [...new Set(fired.map((f) => mountKeyOf(mounts, f)))];
  const key = mountKeyOf(mounts, weapon);
  const name = byId.get(weapon)?.name ?? weapon;
  if (committed.includes(key)) {
    if (!canFollowOnMount(byId, firedOnMountOf(mounts, fired, key), weapon)) {
      return err(
        `barrage: "${name}" can't fire — its mount already fired its primary and rider (a mount fires one primary plus one Auxiliary)`,
      );
    }
  } else {
    return err(`barrage: "${name}" is not on a committed mount (${committed.join(", ")})`);
  }
  const w = byId.get(weapon);
  if (w?.noAttack === true) return err(`barrage: ${noAttackRefusal(w)}`);
  if (w?.loading && !isWeaponLoaded(attacker.current.resources, weapon)) {
    return err(`barrage: "${w.name}" is not loaded (Loading) — reload with Stabilize before firing again`);
  }
  return ok(undefined);
}

/**
 * Why `weapon` can't be the **next shot** of an open attack lock, by pairing alone, or
 * `undefined` when it can — the committed-mount half of the continuation wall, for the menu's
 * fire card. A Barrage reads {@link checkBarrageContinuation} (committed mount, once per weapon,
 * Superheavy alone, one primary plus one Aux per mount); a Skirmish, the lock's one mount and
 * {@link canFollowOnMount}. Weapon state (Loading, Recharge, Limited, Ordnance) is
 * {@link weaponFireBlock}'s, checked separately.
 */
export type LockPairingBlockArgs = {
  state: EncounterState;
  attackerId: string;
  weapon: string;
  lock: Pick<AttackLock, "action" | "weapons" | "mounts">;
};

export function lockPairingBlock(args: LockPairingBlockArgs): string | undefined {
  const { state, attackerId, weapon, lock } = args;
  if (lock.action === "barrage") {
    const v = checkBarrageContinuation({ state, attackerId, weapon, fired: lock.weapons, ...(lock.mounts !== undefined ? { mounts: lock.mounts } : {}) });
    return v.ok ? undefined : v.error;
  }
  if (lock.action !== "skirmish") return undefined;
  const { attacker, byId } = barrageLoadout(state, attackerId);
  const mounts = attacker?.base.mounts;
  const key = mountKeyOf(mounts, weapon);
  const name = byId.get(weapon)?.name ?? weapon;
  if (lock.weapons.includes(weapon) && attacksLeft(byId, lock.weapons, weapon) <= 0) return `skirmish: "${name}" already fired this Skirmish`;
  // A Skirmish holds its first weapon's one mount (the lock's, else read off that weapon).
  const lockMount = lock.mounts?.[0] ?? (lock.weapons[0] !== undefined ? mountKeyOf(mounts, lock.weapons[0]) : undefined);
  if (lockMount !== undefined && key !== lockMount) return `skirmish: "${name}" is not on the Skirmish's mount`;
  if (!canFollowOnMount(byId, firedOnMountOf(mounts, lock.weapons, key), weapon)) {
    return `skirmish: "${name}" can't fire — its mount already fired its primary and rider (a mount fires one primary plus one Auxiliary)`;
  }
  return undefined;
}

/**
 * Is there any weapon left that the in-progress Barrage could still fire? This is the
 * **auto-close predicate** — the room appends `close_attack` after a shot exactly when this returns
 * `false`. `true` iff some weapon passes {@link checkBarrageContinuation} against `lock`
 * AND is not blocked ({@link weaponFireBlock} against the lock's Ordnance snapshot): the lock
 * closes on its own once every fireable weapon on the committed mounts has fired, and blocked
 * weapons (Unloaded, Discharged, out of charges, Ordnance after acting) don't hold it open.
 * **Range is deliberately not consulted** — a still-allowed weapon that happens to be out of range
 * keeps the Barrage open (the player ends it via `end_attack` or converts it). `lock` is the lock
 * as it stands AFTER the shot being folded.
 */
export type BarrageLockAfter = Pick<AttackLock, "weapons" | "mounts" | "ordnanceWindow">;

export function hasLegalBarrageContinuation(state: EncounterState, attackerId: string, lock: BarrageLockAfter): boolean {
  const { weapons } = barrageLoadout(state, attackerId);
  const ordnanceWindow = lock.ordnanceWindow === true;
  return weapons.some((w) => {
    const key = weaponInstanceKey(w);
    const legal = checkBarrageContinuation({
      state,
      attackerId,
      weapon: key,
      fired: lock.weapons,
      ...(lock.mounts !== undefined ? { mounts: lock.mounts } : {}),
    });
    return legal.ok && weaponFireBlock({ state, attackerId, weapon: key, ordnanceWindow }) === undefined;
  });
}

/**
 * The Skirmish twin of {@link hasLegalBarrageContinuation}: is there any weapon the open Skirmish
 * lock could still fire? The room appends `close_attack` after a Skirmish continuation exactly
 * when this returns `false`. A weapon qualifies when {@link lockPairingBlock} admits it (the
 * primary's Aux rider, or a multi-attack weapon with attacks left) and {@link weaponFireBlock}
 * doesn't block it against the lock's Ordnance snapshot. `lock` is the lock as it stands AFTER the
 * shot being folded.
 */
export function hasLegalSkirmishContinuation(state: EncounterState, attackerId: string, lock: BarrageLockAfter): boolean {
  const { weapons } = barrageLoadout(state, attackerId);
  const ordnanceWindow = lock.ordnanceWindow === true;
  const skirmish = { action: "skirmish" as const, weapons: lock.weapons, ...(lock.mounts !== undefined ? { mounts: lock.mounts } : {}) };
  return weapons.some((w) => {
    const key = weaponInstanceKey(w);
    return lockPairingBlock({ state, attackerId, weapon: key, lock: skirmish }) === undefined && weaponFireBlock({ state, attackerId, weapon: key, ordnanceWindow }) === undefined;
  });
}

/**
 * May `attacker` stop its Barrage and **keep it as a Skirmish** (`stop_barrage_as_skirmish`,
 * `decisions/0049`'s 2026-09-24 amendment)? True iff it is the acting actor holding a `barrage`
 * lock, at least one weapon fired and every fired weapon sits on ONE mount (a primary with at
 * most its Auxiliary rider — exactly a legal Skirmish), no Superheavy fired (a Superheavy can
 * only fire in a Barrage, Core p.71 — its lock auto-closes anyway), and Skirmish is still usable
 * this turn (`skirmishUsable`, the caller's once-per-turn read — a turn that already Skirmished
 * can't gain a second one by converting).
 */
export type CheckStopBarrageAsSkirmishArgs = {
  state: EncounterState;
  attackerId: string;
  /** Whether Skirmish is still usable this turn (the once-per-turn ledger, allowance applied). */
  skirmishUsable: boolean;
};

export function checkStopBarrageAsSkirmish(args: CheckStopBarrageAsSkirmishArgs): BudgetVerdict {
  const { state, attackerId, skirmishUsable } = args;
  const turn = state.activation.turn;
  const lock = activeAttack(state.activation);
  if (turn.phase !== "acting" || turn.actor !== attackerId || lock === null || lock.action !== "barrage") {
    return err(`stop barrage: ${attackerId} is not mid-Barrage`);
  }
  if (lock.weapons.length === 0) return err(`stop barrage: nothing has fired yet — cancel instead`);
  const { attacker, byId } = barrageLoadout(state, attackerId);
  if (firedSuperheavy(byId, lock.weapons)) return err(`stop barrage: a Superheavy fires only as part of a Barrage`);
  const mounts = attacker?.base.mounts;
  const fromMounts = new Set(lock.weapons.map((f) => mountKeyOf(mounts, f)));
  if (fromMounts.size !== 1) {
    return err(`stop barrage: weapons on both mounts fired — this is a Barrage now; end it early instead (end_attack)`);
  }
  if (!skirmishUsable) {
    return err(`stop barrage: ${attacker?.name ?? attackerId} has already used Skirmish this turn`);
  }
  return ok(undefined);
}

/**
 * The Full Tech per-pick legality — mirrors {@link checkBarrageOpener}/
 * {@link checkBarrageContinuation}'s per-shot shape, but with no loadout concept: the only
 * structural wall is "is `option` a real Quick Tech id" ({@link quickTechOption}). Range/Sensors/
 * LoS/tech-immunity/self-target/willing-ally are the SAME per-target walls a standalone routed
 * intent gets (`refereeIntent.ts`, reused rather than duplicated) — not this function's job.
 */

